import * as React from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { useEffect, useState } from "react";
import { Label } from "@progress/kendo-react-labels";
import CustomInput from "components/common/Input";
import * as api from "../../api/index";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { DateFormatCell, DateFormat } from "components/common/formatter";
import { Tile } from "components/UIComponents/Tile";
import { TileWrapper } from "components/UIComponents/TileWrapper";
import { createNewContact, getCompanyProfile } from "api";
import { combineAddressLines } from "Helper";
import LoadingScreen from "components/common/Loading";
import { toast } from "react-toastify";
import {
  CategoryBadgeComponent,
  PrimaryBadgeComponent,
} from "components/MatterDetails/ContactCard";
import DataList from "components/MatterDetails/DataList";
import { FormComboBox } from "components/Matters";
import { CONSTANT } from "CONSTANT/Matter/MatterForm";
import { filterBy } from "@progress/kendo-data-query";
import {
  getContactCategoryInfo,
  useActiveContactCategories,
} from "services/contactCategoriesService";
import { Grid, GridColumn } from "@progress/kendo-react-grid";
import FiledStar from "../../assets/stars/filled.png";
import EmptyStar from "../../assets/stars/empty.png";
import ContactInformationComponent from "components/Contact/ContactInformation";
import AutoCompleteInput from "components/AutoCompleteCompanies/AutoCompleteCompany";
import { CompanyItemRender } from "components/AutoCompleteCompanies/CompanyItemRender";
import { emailValidator } from "components/common/validator";
import SubmitButton from "components/common/SubmitButton";
import { Button } from "@progress/kendo-react-buttons";
import Asterisk from "components/common/Asterisk";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { PageTabItemTitle } from "components/tabs/TabItem/PageTabItemTitle";
import { DocumentList } from "components/Documents/DocumentList";
import styles from "./Contact.module.scss";

const MODE = { NEW: 0, VIEW: 1, EDIT: 2 };

const PERSON_CONSTANT = { PERSON: "Person", COMPANY: "Company" };

const typeData = [
  { name: "Person", value: PERSON_CONSTANT.PERSON },
  { name: "Company", value: PERSON_CONSTANT.COMPANY },
];

const MAX_ADDITIONAL_FIELDS = 10;

function emptyAdditionalValues() {
  return {
    additionalValue1: "",
    additionalValue2: "",
    additionalValue3: "",
    additionalValue4: "",
    additionalValue5: "",
    additionalValue6: "",
    additionalValue7: "",
    additionalValue8: "",
    additionalValue9: "",
    additionalValue10: "",
  };
}

function pickAdditionalValuesFromContact(contactData) {
  const valuesBySlot = {};
  for (let i = 1; i <= MAX_ADDITIONAL_FIELDS; i++) {
    const slotValueKey = `additionalValue${i}`;
    const slotValue = contactData?.[slotValueKey];
    valuesBySlot[slotValueKey] = slotValue == null ? "" : String(slotValue);
  }
  return valuesBySlot;
}

function getAdditionalFieldsFromCategory(category) {
  if (!category || typeof category !== "object") return [];
  return Array.from(
    { length: MAX_ADDITIONAL_FIELDS },
    (val, index) => index + 1,
  )
    .map((slot) => {
      const label = String(category?.[`additionalLabel${slot}`] ?? "").trim();
      return label
        ? { slot, label, fieldName: `additionalValue${slot}` }
        : null;
    })
    .filter(Boolean);
}

function formatAdditionalDetailDisplay(value) {
  if (value == null || value === "") return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}/.test(value.trim())) {
    return DateFormat(value);
  }
  return String(value);
}

const VIEW_PERSON_TABS = [
  "Contact Details",
  "Matters",
  "Documents",
  "Additional Details",
];
const VIEW_COMPANY_TABS = [
  "Contact Details",
  "People",
  "Matters",
  "Documents",
  "Additional Details",
];

const AddNewContact = (props) => {
  const [formSubmittig, setFormSubmitting] = useState(false);
  const [type, setType] = useState(PERSON_CONSTANT.PERSON);
  const [selected, setSelected] = React.useState(0);

  const [mode, setMode] = React.useState(
    props.viewMode ? MODE.VIEW : undefined,
  );
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [hasDataChanged, setHasDataChanged] = useState(false);
  const [formProps, setFormProps] = useState({
    allowSubmit: false,
    modified: false,
  });

  const handleSubmit = async (dataItem) => {
    if (mode === MODE.VIEW) {
      setMode(MODE.EDIT);
      return;
    }

    if (
      !comboBoxState[CONSTANT.COMBOBOX.TYPE]?.value ||
      !comboBoxState[CONSTANT.COMBOBOX.CONTACT_LINK]?.categoryName
    ) {
      return;
    }

    setFormSubmitting(true);
    let data = { person: null, company: null };
    data.type = comboBoxState[CONSTANT.COMBOBOX.TYPE]?.value;
    data.contactCategory =
      comboBoxState[CONSTANT.COMBOBOX.CONTACT_LINK]?.categoryName;
    data.contactCategoryId =
      comboBoxState[CONSTANT.COMBOBOX.CONTACT_LINK]?.contactCategoryId;
    data.address = {
      line1: dataItem.line1.split("\n")[0],
      line2: dataItem.line1.split("\n")[1],
      town: dataItem.town,
      state: dataItem.state,
      postcode: dataItem.postcode,
      country: dataItem.country,
    };
    data.email1 = dataItem.email;
    data.phone1 = dataItem.phone1;
    data.phone2 = dataItem.phone2;
    data.mobileNo = dataItem.mobileNo;
    data.website = dataItem.website;
    data.active = true;
    for (let i = 1; i <= MAX_ADDITIONAL_FIELDS; i++) {
      const k = `additionalValue${i}`;
      data[k] = dataItem[k] ?? "";
    }

    if (data.type === PERSON_CONSTANT.PERSON) {
      if (!dataItem.firstName) {
        setFormSubmitting(false);
        return;
      }
      data.person = {
        title: dataItem.title,
        firstName: dataItem.firstName,
        middleName: dataItem.middleName,
        lastName: dataItem.lastName,
        letterTitle: "",
        jobTitle: "",
        nameAtBirth: dataItem.nameAtBirth,
        placeOfBirth: dataItem.placeOfBirth,
        dateOfBirth: dataItem.dateOfBirth || null,
        dateOfDeath: dataItem.dateOfDeath || null,
        ethnicity: 0,
        gender:
          dataItem.gender === "Male"
            ? "M"
            : dataItem.gender === "Female"
              ? "F"
              : "",
        disability: 1,
        niNumber: dataItem.niNumber,
        other1: "",
      };
    }

    if (data.type === PERSON_CONSTANT.COMPANY) {
      if (!dataItem.companyName) {
        setFormSubmitting(false);
        return;
      }

      data.company = {
        companyName: dataItem.companyName,
        companyRegNo: dataItem.companyRegNo,
        corrEmail: dataItem.ccemail,
        corrFirstName: dataItem.ccFirstName,
        corrPhone: dataItem.ccphone1,
        corrJobTitle: dataItem?.ccJobTitle,
        vatNo: dataItem.vatNo,
        sicCode: dataItem.sicCode,
        registeredAddress: {
          line1: dataItem.registeredAddressline1?.split("\n")[0],
          line2: dataItem.registeredAddressline1?.split("\n")[1],
          town: dataItem.registeredAddresstown,
          state: dataItem.registeredAddressstate,
          postcode: dataItem.registeredAddresspostcode,
          country: dataItem.registeredAddresscountry,
        },
        correspondenceContact: {
          phone1: dataItem.ccphone1,
          phone2: dataItem.ccphone2,
          mobileNo: dataItem.ccmobileNo,
          email1: dataItem.ccemail,
          active: true,
          type: PERSON_CONSTANT.PERSON,
          address: {
            line1: dataItem.ccline1?.split("\n")[0],
            line2: dataItem.ccline1?.split("\n")[1],
            town: dataItem.cctown,
            state: dataItem.ccstate,
            postcode: dataItem.ccpostcode,
            country: dataItem.cccountry,
          },
          person: {
            title: dataItem.cctitle,
            firstName: dataItem.ccFirstName,
            middleName: dataItem.ccmiddleName,
            lastName: dataItem.cclastName,
          },
          website: dataItem.ccwebsite,
        },
      };
    }
    try {
      if (props.viewMode) {
        await api.editContact(props.contactId, data);
      } else {
        const response = await createNewContact(data, type);
        const newContactId = response?.data?.data?.contactId;
        if (props.onContactAdded && newContactId) {
          props.onContactAdded(newContactId, response.data.data);
        }
      }
      setFormSubmitting(false);
      toast.success(
        props.viewMode
          ? "Contact Updated Successfully"
          : "Contact Created Successfully",
      );
      setHasDataChanged(true);
      if (props.contactId && mode === MODE.EDIT) {
        getContact(props.contactId);
      } else if (!props.viewMode) {
        props.close(true);
      }
      setMode(MODE.VIEW);
    } catch (error) {
      setFormSubmitting(false);
    }
  };

  // Reset tab index whenever mode changes to avoid out-of-range selection
  useEffect(() => {
    setSelected(0);
  }, [mode]);

  const handleComboBoxChange = (e, label) => {
    setComboBoxState({ ...comboBoxState, [label]: e.value });
  };

  const [personState, setPersonState] = React.useState({
    title: "",
    dateOfBirth: "",
    dateOfDeath: "",
    disability: "",
    ethnicity: "",
    firstName: "",
    gender: "",
    jobTitle: "",
    lastName: "",
    letterTitle: "",
    middleName: "",
    nameAtBirth: "",
    niNumber: "",
    other1: "",
    placeOfBirth: "",
  });

  const [address, setAddress] = React.useState({
    country: "",
    line1: "",
    line2: "",
    postcode: "",
    state: "",
    town: "",
  });

  const [companyState, setCompanyState] = React.useState({
    companyName: "",
    companyRegNo: "",
    vatNo: "",
    sicCode: "",
    other1: "",
    registeredAddress: {
      line1: "",
      line2: "",
      town: "",
      state: "",
      postcode: "",
      country: "",
    },
    ccemail1: "",
    ccemail2: "",
    ccphone1: "",
    ccphone2: "",
    ccmobileNo: "",
    ccwebsite: "",
    ccnotes: "",
    active: true,
    type: "Company",
    address: {
      ccline1: "",
      ccline2: "",
      cctown: "",
      ccstate: "",
      ccpostcode: "",
      cccountry: "",
    },
    ccPerson: { title: "", firstName: "", middleName: "", lastName: "" },
  });

  const [initialValues, setInitialValues] = React.useState({});

  const [additionalValues, setAdditionalValues] = useState(() =>
    emptyAdditionalValues(),
  );
  const [categoryAdditionalFields, setCategoryAdditionalFields] = useState([]);

  const prevCategoryIdRef = React.useRef(undefined);

  const [state, setState] = React.useState({
    email: "",
    phone1: "",
    phone2: "",
    mobileNo: "",
    website: "",
    notes: "",
    other1: "",
    active: true,
    type: "Company",
  });
  const [comboBoxState, setComboBoxState] = useState({
    [CONSTANT.COMBOBOX.CONTACT_LINK]: "",
    [CONSTANT.COMBOBOX.TYPE]: "",
  });

  const selectedCategoryId =
    comboBoxState[CONSTANT.COMBOBOX.CONTACT_LINK]?.contactCategoryId;

  useEffect(() => {
    if (!selectedCategoryId) {
      setCategoryAdditionalFields([]);
      return;
    }
    let cancelled = false;
    getContactCategoryInfo(selectedCategoryId)
      .then(({ data }) => {
        if (!cancelled) {
          setCategoryAdditionalFields(
            getAdditionalFieldsFromCategory(data?.data),
          );
        }
      })
      .catch(() => {
        if (!cancelled) setCategoryAdditionalFields([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCategoryId]);

  useEffect(() => {
    if (mode === MODE.VIEW) {
      prevCategoryIdRef.current = selectedCategoryId;
      return;
    }
    if (
      prevCategoryIdRef.current !== undefined &&
      selectedCategoryId !== undefined &&
      prevCategoryIdRef.current !== selectedCategoryId
    ) {
      setAdditionalValues(emptyAdditionalValues());
    }
    prevCategoryIdRef.current = selectedCategoryId;
  }, [selectedCategoryId, mode]);

  // Reset tab when contact type changes (tab list differs between Person and Company)
  const selectedContactType = comboBoxState[CONSTANT.COMBOBOX.TYPE]?.value;
  useEffect(() => {
    if (mode !== MODE.VIEW) {
      setSelected(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedContactType]);

  const [contactData, setContactData] = useState();
  const [gridData, setGridData] = useState([]);
  const [mattersLoaded, setMattersLoaded] = useState(false);
  const [peopleData, setPeopleData] = useState([]);
  const [contactDataFilter, setContactDataFilter] = useState();

  const [loading, setLoading] = React.useState(
    mode === MODE.VIEW ? true : false,
  );
  const [companyProfile, setCompanyProfile] = useState();

  const { data: activeContactCategories = [] } = useActiveContactCategories();

  useEffect(() => {
    setContactData(activeContactCategories);
    setContactDataFilter(activeContactCategories.slice());
  }, [activeContactCategories]);

  const getContact = (contactId) => {
    setLoading(true);
    try {
      api.getSingleContact(contactId).then(({ data }) => {
        setState({
          ...state,
          email: data?.data?.email1,
          mobileNo: data?.data?.mobileNo,
          phone1: data?.data?.phone1,
          phone2: data?.data?.phone2,
          website: data?.data?.website,
          contactCategory: data?.data?.contactCategory,
        });
        setAddress({
          country: data?.data?.address?.country,
          line1: combineAddressLines(
            data?.data?.address?.line1,
            data?.data?.address?.line2,
          ),
          postcode: data?.data?.address?.postcode,
          state: data?.data?.address?.state,
          town: data?.data?.address?.town,
        });
        setComboBoxState({
          [CONSTANT.COMBOBOX.TYPE]: {
            name: data?.data?.type,
            value: data?.data?.type,
          },
          [CONSTANT.COMBOBOX.CONTACT_LINK]: {
            categoryName: data?.data?.contactCategory,
            contactCategoryId: data?.data?.contactCategoryId,
          },
        });
        setAdditionalValues(pickAdditionalValuesFromContact(data?.data));
        if (data?.data?.type === PERSON_CONSTANT.PERSON) {
          setType(PERSON_CONSTANT.PERSON);
          setPersonState({
            title: data.data?.person?.title,
            dateOfBirth: data?.data?.person?.dateOfBirth
              ? new Date(data?.data?.person?.dateOfBirth)
              : "",
            dateOfDeath: data?.data?.person?.dateOfDeath
              ? new Date(data.data.person.dateOfDeath)
              : "",
            disability: data?.data?.person?.disability,
            ethnicity: data?.data?.person?.ethnicity,
            firstName: data?.data?.person?.firstName,
            gender: data?.data?.person?.gender === "F" ? "Female" : "Male",
            lastName: data?.data?.person?.lastName,
            middleName: data?.data?.person?.middleName,
            nameAtBirth: data?.data?.person?.nameAtBirth,
            name: data?.data?.name,
            niNumber: data?.data?.person?.niNumber,
            placeOfBirth: data?.data?.person?.placeOfBirth,
          });
          setLoading(false);
        } else if (data?.data?.type === PERSON_CONSTANT.COMPANY) {
          setType(PERSON_CONSTANT.COMPANY);
          setCompanyState({
            companyName: data.data?.company?.companyName,
            companyRegNo: data.data?.company?.companyRegNo,
            vatNo: data.data?.company?.vatNo,
            sicCode: data.data?.company?.sicCode,
            registeredAddressline1:
              data.data?.company?.registeredAddress?.line1 +
              "\n" +
              data.data?.company?.registeredAddress?.line2,
            registeredAddresscountry:
              data?.data.company?.registeredAddress?.country,
            registeredAddresspostcode:
              data?.data.company?.registeredAddress?.postcode,
            registeredAddressstate:
              data?.data.company?.registeredAddress?.state,
            registeredAddresstown: data?.data.company?.registeredAddress?.town,
            ccline1:
              data?.data?.address?.line1 +
              "\n" +
              data.data.company?.correspondenceContact?.address?.line2,
            cccountry: data?.data?.company?.address?.country,
            ccpostcode: data?.data.address?.postcode,
            ccstate: data?.data?.address?.state,
            cctown: data?.data?.address?.town,
            ccemail: data?.data?.company?.corrEmail,
            ccJobTitle: data?.data?.company?.corrJobTitle,
            ccmobileNo: data?.data?.mobileNo,
            ccphone1: data?.data?.company?.corrPhone,
            ccphone2: data?.data?.phone2,
            cctitle: data?.data?.company?.corrTitle,
            ccFirstName: data?.data.company?.corrFirstName,
            ccmiddleName: data?.data.company?.middleName,
            cclastName: data?.data.company?.corrLastName,
          });
          setLoading(false);
        }
      });
    } catch (error) {
      props.close();
    }
  };

  const getContactGridData = async () => {
    setMattersLoaded(false);
    try {
      const data = await api.getContactsMatterData(props.contactId);
      setGridData(data?.data?.data ?? []);
    } catch (error) {
      setGridData([]);
    } finally {
      setMattersLoaded(true);
    }
  };

  const getContactPeopleData = async () => {
    try {
      const data = await api.getContactDetails(props.contactId);
      const rows = data?.data?.data ?? [];
      setPeopleData(rows);
    } catch (error) {
      setPeopleData([]);
    }
  };

  const NameCell = (e) => {
    return (
      <td className="gridBoldField">
        <span style={{ color: "black" }}>{e.dataItem[e.field]}</span>
      </td>
    );
  };

  const PeopleNameCell = (e) => {
    const first = e.dataItem?.correspondenceFirstName ?? "";
    const last = e.dataItem?.correspondenceLastName ?? "";
    const name = `${first} ${last}`.trim();
    return (
      <td className="gridBoldField">
        <span style={{ color: "black" }}>{name}</span>
      </td>
    );
  };

  const StarCell = (e) => {
    const coloredStars = Array.apply(null, Array(e.dataItem.rating || 0)).map(
      function (y, i) {
        return i;
      },
    );
    const unColoredStars = Array.apply(null, Array(5 - e.dataItem.rating)).map(
      function (y, i) {
        return i;
      },
    );
    return (
      <td className="">
        {coloredStars.map((item) => (
          <img
            key={item}
            src={FiledStar}
            className="me-1 mb-1"
            style={{ height: "14px", width: "14px" }}
            alt="FiledStar"
          />
        ))}
        {unColoredStars.map((item) => (
          <img
            key={item}
            src={EmptyStar}
            className="me-1 mb-1"
            style={{ height: "14px", width: "14px" }}
            alt="EmptyStar"
          />
        ))}
      </td>
    );
  };

  useEffect(() => {
    if (companyProfile) {
      let selectedAddress =
        companyProfile.service_address ||
        companyProfile.registered_office_address ||
        {};

      let newAddress = {
        country: selectedAddress.country || "",
        line1: combineAddressLines(
          selectedAddress.premises,
          selectedAddress.address_line_1,
        ),
        line2: selectedAddress.address_line_2 || "",
        postcode: selectedAddress.postal_code || "",
        state: selectedAddress.region || "",
        town: selectedAddress.locality || "",
      };

      setInitialValues({
        ...state,
        companyName: companyProfile.company_name || "",
        ...newAddress,
        ...additionalValues,
      });
    } else if (type === PERSON_CONSTANT.PERSON) {
      setInitialValues({
        ...state,
        ...personState,
        ...address,
        ...additionalValues,
      });
    } else {
      setInitialValues({
        ...state,
        ...companyState,
        ...address,
        ...additionalValues,
      });
    }
  }, [personState, companyState, type, companyProfile, additionalValues]);

  useEffect(() => {
    setIsSubmitted(false);
  }, [comboBoxState[CONSTANT.COMBOBOX.TYPE]?.value]);

  const filterCaegory = (filter) => {
    const data = (contactData || []).slice();
    return filterBy(data, filter);
  };
  const cateforyFilterChange = (event) => {
    setContactDataFilter(filterCaegory(event.filter));
  };

  useEffect(() => {
    if (props.viewMode) {
      setMode(MODE.VIEW);
      getContact(props.contactId);
      getContactGridData();
      getContactPeopleData();
    } else {
      setMode(MODE.NEW);
      setComboBoxState({
        ...comboBoxState,
        [CONSTANT.COMBOBOX.TYPE]: { name: "Person", value: "Person" },
      });
    }
  }, [props.viewMode]);

  const handleSelection = async (event) => {
    const response = await getCompanyProfile(event);
    setCompanyProfile(response?.data);
  };

  const isCompanyType =
    comboBoxState[CONSTANT.COMBOBOX.TYPE]?.value === "Company";
  const baseEditTabs = [
    "Contact Details",
    "Matters",
    "Documents",
    "Additional Details",
  ];

  const contactDocumentList = (
    <DocumentList
      contactId={props.contactId}
      component="contact"
      tileHeading="Documents"
      canModify={props.canModify}
    />
  );
  const editTabs =
    mode === MODE.NEW
      ? baseEditTabs.filter((t) => t !== "Matters" && t !== "Documents")
      : baseEditTabs;
  const currentTabs =
    mode === MODE.VIEW
      ? type === PERSON_CONSTANT.COMPANY
        ? VIEW_COMPANY_TABS
        : VIEW_PERSON_TABS
      : editTabs;

  const MattersGrid = () => (
    <Tile
      heading="Matters"
      className={`p-3 px-4 pt-2 pb-0 overflow-auto ${styles.tile}`}
    >
      <Grid
        data={gridData}
        className="fs-md mb-1 mb-xxl-2"
        style={{ minHeight: "83%" }}
      >
        <GridColumn
          field="matterRef"
          title="Matter Ref"
          headerClassName="ps-2"
          cell={NameCell}
          width={"140px"}
        />
        <GridColumn field="matterDetails" title="Details" />
        <GridColumn field="clientName" title="Client" />
        <GridColumn
          field="startDate"
          title="Opened"
          cell={DateFormatCell}
          width="140px"
        />
        <GridColumn
          field="dateCompleted"
          title="Closed"
          cell={DateFormatCell}
          width="120px"
        />
        <GridColumn
          field="ratings"
          title="Ratings"
          cell={StarCell}
          width={"120px"}
        />
      </Grid>
    </Tile>
  );

  const PeopleGrid = () => (
    <Grid
      data={peopleData}
      className="fs-md grid-minwidth-col4-200 mb-1 mb-xxl-2"
      style={{ minHeight: "83%" }}
    >
      <GridColumn
        field="correspondenceFirstName"
        title="Name"
        headerClassName="ps-4"
        cell={PeopleNameCell}
      />
      <GridColumn field="correspondencePhone" title="Phone" />
      <GridColumn field="correspondenceJobTitle" title="Job Title" />
      <GridColumn field="correspondenceEmail" title="Email" />
      <GridColumn field="rating" title="Ratings" cell={StarCell} />
    </Grid>
  );

  return (
    <Dialog
      title={
        mode === MODE.VIEW
          ? "View Contact"
          : mode === MODE.EDIT
            ? "Edit Contact"
            : "Add Contact"
      }
      onClose={() => props.close(hasDataChanged)}
      className="dialog-xxl"
    >
      <div>
        {mode === MODE.VIEW ? (
          loading ? (
            <LoadingScreen />
          ) : (
            <div className="pt-3 px-4">
              <TabStrip
                selected={selected}
                onSelect={(e) => setSelected(e.selected)}
                keepTabsMounted={true}
                className="mt-3 px-3 pagetab pagetab-client"
              >
                {currentTabs.map((tabName, index) => (
                  <TabStripTab
                    title={
                      <PageTabItemTitle
                        client={true}
                        title={tabName}
                        index={index}
                        selected={selected}
                      />
                    }
                    key={index}
                  >
                    <TileWrapper>
                      {tabName === "Contact Details" &&
                        type === PERSON_CONSTANT.PERSON && (
                          <div className="row g-2">
                            <div className="col">
                              <Tile
                                heading="Contact"
                                className={`p-3 ps-4 pe-4 pt-2 pb-0 overflow-auto ${styles.tile}`}
                                headingItems={
                                  <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                      <CategoryBadgeComponent
                                        text={initialValues.contactCategory}
                                        className="mt-3 ps-3 pe-3 me-2"
                                      />
                                    </div>
                                    <div>
                                      <PrimaryBadgeComponent
                                        text={"Person"}
                                        className="mt-3 ps-3 pe-3"
                                      />
                                    </div>
                                  </div>
                                }
                              >
                                <div className="px-2">
                                  <div>
                                    <div style={{ width: "30%" }}>
                                      <DataList
                                        text={"Name:"}
                                        value={initialValues?.name}
                                        keyStyles={{ minWidth: "100px" }}
                                        className={"w-100 mt-1"}
                                      />
                                    </div>
                                    <div className="d-flex align-items-start justify-content-start mt-3">
                                      <div style={{ width: "280px" }}>
                                        <DataList
                                          text={"Email:"}
                                          value={initialValues.email}
                                          keyClass={"mb-2"}
                                          alignItems="align-items-start"
                                          valueClass={"mb-2"}
                                          keyStyles={{ minWidth: "100px" }}
                                        />
                                        <DataList
                                          text={"Address:"}
                                          className={"mt-2"}
                                          alignItems="align-items-start"
                                          keyStyles={{ minWidth: "100px" }}
                                          value={
                                            <>
                                              {initialValues?.line1 && (
                                                <>
                                                  {initialValues?.line1} <br />
                                                </>
                                              )}
                                              {initialValues?.town && (
                                                <>
                                                  {initialValues?.town} <br />
                                                </>
                                              )}
                                              {initialValues?.state && (
                                                <>
                                                  {initialValues?.state} <br />
                                                </>
                                              )}
                                              {initialValues?.country && (
                                                <>
                                                  {initialValues?.country}{" "}
                                                  <br />
                                                </>
                                              )}
                                            </>
                                          }
                                          keyClass={"mb-2"}
                                          valueClass={"mb-2"}
                                        />
                                      </div>
                                      <div className="ps-5">
                                        <DataList
                                          text={"Phone 1:"}
                                          value={initialValues.phone1}
                                          keyClass={"mb-2"}
                                          valueClass={"mb-2"}
                                          keyStyles={{ minWidth: "100px" }}
                                        />
                                        <DataList
                                          text={"Phone 2:"}
                                          className={"mt-2"}
                                          value={initialValues.phone2}
                                          keyClass={"mb-2"}
                                          valueClass={"mb-2"}
                                          keyStyles={{ minWidth: "100px" }}
                                        />
                                        <DataList
                                          text={"Mobile:"}
                                          className={"mt-2"}
                                          value={initialValues.mobileNo}
                                          keyClass={"mb-2"}
                                          keyStyles={{ minWidth: "100px" }}
                                          valueClass={"mb-2"}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </Tile>
                            </div>
                          </div>
                        )}

                      {tabName === "Contact Details" &&
                        type === PERSON_CONSTANT.COMPANY && (
                          <div className="row g-2">
                            <div className="col-12 col-xl-7">
                              <Tile
                                heading="Contact"
                                className={`p-3 ps-4 pe-4 pt-2 pb-0 overflow-auto ${styles.tile}`}
                                headingItems={
                                  <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                      <CategoryBadgeComponent
                                        text={initialValues.contactCategory}
                                        className="mt-3 ps-3 pe-3 me-2"
                                      />
                                    </div>
                                    <div>
                                      <PrimaryBadgeComponent
                                        text={"Company"}
                                        className="mt-3 ps-3 pe-3"
                                      />
                                    </div>
                                  </div>
                                }
                              >
                                <div className="px-2">
                                  <div>
                                    <div style={{ width: "50%" }}>
                                      <DataList
                                        text={"Name:"}
                                        value={initialValues?.companyName}
                                        className={
                                          "w-100 mt-1 justify-content-start"
                                        }
                                        keyStyles={{ minWidth: "80px" }}
                                      />
                                    </div>
                                    <div className="d-flex align-items-start justify-content-start mt-3">
                                      <div className="">
                                        <DataList
                                          text={"Email:"}
                                          value={initialValues.email}
                                          keyClass={"mb-2"}
                                          alignItems="align-items-start"
                                          valueClass={"mb-2"}
                                          keyStyles={{ minWidth: "80px" }}
                                        />
                                        <DataList
                                          text={"Address:"}
                                          className={"mt-2"}
                                          alignItems="align-items-start"
                                          value={
                                            <>
                                              {initialValues?.ccline1 && (
                                                <>
                                                  {initialValues?.ccline1}{" "}
                                                  <br />
                                                </>
                                              )}
                                              {initialValues?.cctown && (
                                                <>
                                                  {initialValues?.cctown} <br />
                                                </>
                                              )}
                                              {initialValues?.ccstate && (
                                                <>
                                                  {initialValues?.ccstate}{" "}
                                                  <br />
                                                </>
                                              )}
                                              {initialValues?.cccountry && (
                                                <>
                                                  {initialValues?.cccountry}{" "}
                                                  <br />
                                                </>
                                              )}
                                            </>
                                          }
                                          keyClass={"mb-2"}
                                          valueClass={"mb-2"}
                                          keyStyles={{ minWidth: "80px" }}
                                        />
                                      </div>
                                      <div className="ps-5">
                                        <DataList
                                          text={"Phone 1:"}
                                          value={initialValues.phone1}
                                          keyClass={"mb-2"}
                                          keyStyles={{ minWidth: "80px" }}
                                          valueClass={"mb-2"}
                                        />
                                        <DataList
                                          text={"Phone 2:"}
                                          className={"mt-2"}
                                          value={initialValues.phone2}
                                          keyClass={"mb-2"}
                                          valueClass={"mb-2"}
                                          keyStyles={{ minWidth: "80px" }}
                                        />
                                        <DataList
                                          text={"Mobile:"}
                                          className={"mt-2"}
                                          value={initialValues.mobileNo}
                                          keyClass={"mb-2"}
                                          valueClass={"mb-2"}
                                          keyStyles={{ minWidth: "80px" }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </Tile>
                            </div>
                            <div className="col-12 col-xl-5">
                              <Tile
                                className={`p-3 ps-4 pe-4 pt-2 pb-0 overflow-auto ms-2 ${styles.tile}`}
                                heading="Correspondence"
                              >
                                <div className="px-2">
                                  <DataList
                                    text={"Name:"}
                                    value={initialValues?.ccFirstName}
                                    valueClass={"mb-2"}
                                    keyClass={"mb-2"}
                                    className={"w-100 mt-3"}
                                    keyStyles={{ minWidth: "80px" }}
                                  />
                                  <DataList
                                    text={"Phone:"}
                                    valueClass={"mb-2"}
                                    keyClass={"mb-2"}
                                    value={initialValues?.ccphone1}
                                    className={"w-100 mt-2"}
                                    keyStyles={{ minWidth: "80px" }}
                                  />
                                  <DataList
                                    text={"Email:"}
                                    valueClass={"mb-2"}
                                    keyClass={"mb-2"}
                                    value={initialValues?.ccemail}
                                    className={"w-100 mt-2"}
                                    keyStyles={{ minWidth: "80px" }}
                                  />
                                  <DataList
                                    text={"Job Title:"}
                                    valueClass={"mb-2"}
                                    keyClass={"mb-2"}
                                    value={initialValues?.ccJobTitle}
                                    className={"w-100 mt-2"}
                                    keyStyles={{ minWidth: "80px" }}
                                  />
                                </div>
                              </Tile>
                            </div>
                          </div>
                        )}

                      {tabName === "Matters" && <MattersGrid />}

                      {tabName === "People" &&
                        type === PERSON_CONSTANT.COMPANY && (
                          <Tile
                            heading="People"
                            className={`p-3 px-4 pt-2 pb-0 overflow-auto ${styles.tile}`}
                          >
                            <PeopleGrid />
                          </Tile>
                        )}

                      {tabName === "Documents" && contactDocumentList}

                      {tabName === "Additional Details" &&
                        (categoryAdditionalFields.length > 0 ? (
                          <Tile
                            heading="Additional Details"
                            className={`p-3 px-4 pt-2 pb-4 overflow-auto ${styles.tile}`}
                          >
                            {categoryAdditionalFields.map(
                              ({ label, fieldName }) => (
                                <DataList
                                  key={fieldName}
                                  text={`${label}:`}
                                  value={formatAdditionalDetailDisplay(
                                    initialValues[fieldName],
                                  )}
                                  keyClass={"mb-2"}
                                  valueClass={"mb-2"}
                                  className={"w-100 mt-2"}
                                  keyStyles={{ minWidth: "140px" }}
                                />
                              ),
                            )}
                          </Tile>
                        ) : (
                          <Tile
                            heading="Additional Details"
                            className={`p-3 px-4 pt-2 pb-0 overflow-auto ${styles.tile}`}
                          >
                            <div className="d-flex justify-content-center align-items-center py-3 text-muted fs-md">
                              No additional details for this contact category.
                            </div>
                          </Tile>
                        ))}
                    </TileWrapper>
                  </TabStripTab>
                ))}
              </TabStrip>
            </div>
          )
        ) : (
          /* ── ADD / EDIT mode ── */
          <div className="pt-3 px-4">
            <Form
              ignoreModified={mode == MODE.NEW ? true : false}
              initialValues={initialValues}
              onSubmit={handleSubmit}
              id="contact-form"
              key={JSON.stringify(initialValues)}
              render={(formRenderProps) => {
                if (
                  formRenderProps.allowSubmit !== formProps.allowSubmit ||
                  formRenderProps.modified !== formProps.modified
                ) {
                  setFormProps({
                    allowSubmit: formRenderProps.allowSubmit,
                    modified: formRenderProps.modified,
                  });
                }
                return (
                  <FormElement>
                    <TabStrip
                      selected={selected}
                      onSelect={(e) => setSelected(e.selected)}
                      keepTabsMounted={true}
                      className="mt-3 px-3 pagetab pagetab-client"
                      animation={false}
                    >
                      {editTabs.map((tabName, index) => (
                        <TabStripTab
                          title={
                            <PageTabItemTitle
                              client={true}
                              title={tabName}
                              index={index}
                              selected={selected}
                            />
                          }
                          key={index}
                        >
                          <TileWrapper>
                            {tabName === "Contact Details" && (
                              <div className="row g-2">
                                <div
                                  className={
                                    isCompanyType ? "col-12 col-xl-7" : "col-12"
                                  }
                                >
                                  <Tile
                                    heading="Contact Details"
                                    className={`p-3 px-4 pt-2 pb-4 overflow-auto ${styles.addTitle}`}
                                  >
                                    <div className="container">
                                      <div className="mt-2">
                                        <FormComboBox
                                          wf={true}
                                          isStyled={true}
                                          width={"260px"}
                                          isFiltered={false}
                                          className={`mt-2 ${isSubmitted &&
                                            !comboBoxState[
                                              CONSTANT.COMBOBOX.TYPE
                                            ]?.value
                                            ? "validation-invalid"
                                            : ""
                                            }`}
                                          disabled={props.viewMode}
                                          onChangeComboBox={(e, name) => {
                                            formRenderProps.onChange("ok", {
                                              value: 1,
                                            });
                                            handleComboBoxChange(e, name);
                                          }}
                                          data={typeData}
                                          boxShadow={true}
                                          state={comboBoxState}
                                          textField="name"
                                          name={CONSTANT.COMBOBOX.TYPE}
                                        />
                                      </div>
                                      <div className="mt-3">
                                        <FormComboBox
                                          wf={true}
                                          isStyled={true}
                                          width={"260px"}
                                          className={`mt-2 ${isSubmitted &&
                                            !comboBoxState[
                                              CONSTANT.COMBOBOX.CONTACT_LINK
                                            ]?.categoryName
                                            ? "validation-invalid"
                                            : ""
                                            }`}
                                          onChangeComboBox={(e, name) => {
                                            formRenderProps.onChange("ok", {
                                              value: 1,
                                            });
                                            handleComboBoxChange(e, name);
                                          }}
                                          onFilterChange={cateforyFilterChange}
                                          data={contactDataFilter}
                                          state={comboBoxState}
                                          boxShadow={true}
                                          textField="categoryName"
                                          label={"Category"}
                                          name={CONSTANT.COMBOBOX.CONTACT_LINK}
                                          showAsterisk={
                                            !comboBoxState[
                                              CONSTANT.COMBOBOX.CONTACT_LINK
                                            ]?.categoryName
                                          }
                                        />
                                      </div>

                                      {/* Company name field */}
                                      {comboBoxState[CONSTANT.COMBOBOX.TYPE]
                                        ?.value === "Company" && (
                                          <div className="d-flex mt-3">
                                            <div
                                              className={`${isSubmitted &&
                                                !formRenderProps.valueGetter(
                                                  "companyName",
                                                )
                                                ? "validation-invalid"
                                                : ""
                                                }`}
                                            >
                                              {mode === MODE.NEW ? (
                                                <div className="d-flex flex-column">
                                                  <Label>
                                                    Company Name
                                                    {!formRenderProps.valueGetter(
                                                      "companyName",
                                                    ) && <Asterisk />}
                                                  </Label>
                                                  <Field
                                                    disabled={mode === MODE.VIEW}
                                                    name="companyName"
                                                    ph="Company Name"
                                                    component={AutoCompleteInput}
                                                    itemRender={CompanyItemRender(
                                                      handleSelection,
                                                    )}
                                                    formRenderProps={
                                                      formRenderProps
                                                    }
                                                  />
                                                </div>
                                              ) : (
                                                <div className="d-flex flex-column">
                                                  <Label className="label mb-1">
                                                    Company Name
                                                    {!formRenderProps.valueGetter(
                                                      "companyName",
                                                    ) && <Asterisk />}
                                                  </Label>
                                                  <Field
                                                    disabled={mode === MODE.VIEW}
                                                    name="companyName"
                                                    label=""
                                                    ph="Company Name"
                                                    width={"260px"}
                                                    style={{ height: "32px" }}
                                                    component={CustomInput}
                                                  />
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        )}

                                      {/* Person name fields */}
                                      {comboBoxState[CONSTANT.COMBOBOX.TYPE]
                                        ?.value === "Person" && (
                                          <div className="d-flex mt-3">
                                            <div
                                              className="me-4"
                                              style={{ width: "260px" }}
                                            >
                                              <div>
                                                <Label className="label">
                                                  First Name
                                                </Label>
                                                {!formRenderProps.valueGetter(
                                                  "firstName",
                                                ) && <Asterisk />}
                                              </div>
                                              <Field
                                                name={`firstName`}
                                                label=""
                                                width={"260px"}
                                                style={{ height: "32px" }}
                                                ph="Add Name"
                                                component={CustomInput}
                                                className={`${isSubmitted &&
                                                  !formRenderProps.valueGetter(
                                                    "firstName",
                                                  )
                                                  ? "validation-invalid"
                                                  : ""
                                                  }`}
                                              />
                                            </div>
                                            <div>
                                              <Field
                                                name={`lastName`}
                                                label="Last Name"
                                                width={"260px"}
                                                ph="Add Name"
                                                component={CustomInput}
                                              />
                                            </div>
                                          </div>
                                        )}

                                      <ContactInformationComponent
                                        readOnly={mode === MODE.VIEW}
                                        addType={""}
                                        type={
                                          comboBoxState[CONSTANT.COMBOBOX.TYPE]
                                            ?.value
                                        }
                                      />
                                    </div>
                                  </Tile>
                                </div>

                                {isCompanyType && (
                                  <div className="col-12 col-xl-5">
                                    <Tile
                                      heading="Correspondence Person"
                                      className={`p-3 px-4 pt-2 pb-4 overflow-auto ${styles.addTitle}`}
                                    >
                                      <div className="container">
                                        <div className="row mt-3">
                                          <div className="col-auto">
                                            <Field
                                              disabled={mode === MODE.VIEW}
                                              name="ccFirstName"
                                              label="Name"
                                              ph="Enter Name"
                                              width={"260px"}
                                              component={CustomInput}
                                            />
                                          </div>
                                        </div>
                                        <div className="row mt-3">
                                          <div className="col-auto">
                                            <Field
                                              disabled={mode === MODE.VIEW}
                                              name="ccphone1"
                                              label="Phone Number"
                                              ph="Enter Phone Number"
                                              width={"260px"}
                                              type="number"
                                              component={CustomInput}
                                            />
                                          </div>
                                        </div>
                                        <div className="row mt-3">
                                          <div className="col-auto">
                                            <Field
                                              disabled={mode === MODE.VIEW}
                                              name="ccemail"
                                              label="Email"
                                              ph="Enter Email"
                                              width={"260px"}
                                              component={CustomInput}
                                              validator={emailValidator}
                                            />
                                          </div>
                                        </div>
                                        <div className="row mt-3">
                                          <div className="col-auto">
                                            <Field
                                              disabled={mode === MODE.VIEW}
                                              name="ccJobTitle"
                                              label="Job Title"
                                              ph="Enter Job Title"
                                              width={"260px"}
                                              component={CustomInput}
                                            />
                                          </div>
                                        </div>
                                      </div>
                                    </Tile>
                                  </div>
                                )}
                              </div>
                            )}

                            {tabName === "Matters" && <MattersGrid />}

                            {tabName === "Documents" && contactDocumentList}

                            {tabName === "Additional Details" &&
                              (categoryAdditionalFields.length > 0 ? (
                                <Tile
                                  heading="Additional Details"
                                  className={`p-3 px-4 pt-2 pb-4 overflow-auto ${styles.tile}`}
                                >
                                  <div className="container">
                                    {categoryAdditionalFields.map(
                                      ({ label, fieldName }) => (
                                        <div
                                          className="row mt-3"
                                          key={fieldName}
                                        >
                                          <div className="col-auto">
                                            <Field
                                              disabled={mode === MODE.VIEW}
                                              name={fieldName}
                                              label={label}
                                              ph={`Enter ${label}`}
                                              width={"260px"}
                                              component={CustomInput}
                                            />
                                          </div>
                                        </div>
                                      ),
                                    )}
                                  </div>
                                </Tile>
                              ) : (
                                <Tile
                                  heading="Additional Details"
                                  className={`p-3 px-4 pt-2 pb-4 overflow-auto ${styles.tile}`}
                                >
                                  <div className="d-flex justify-content-center align-items-center py-3 text-muted fs-md">
                                    {selectedCategoryId
                                      ? "Additional details are not configured for this category."
                                      : "Select a contact category to see additional fields."}
                                  </div>
                                </Tile>
                              ))}
                          </TileWrapper>
                        </TabStripTab>
                      ))}
                    </TabStrip>
                    <button
                      id="contact-form-submit"
                      type="submit"
                      form="contact-form"
                      style={{ visibility: "hidden" }}
                    />
                  </FormElement>
                );
              }}
            />
          </div>
        )}
      </div>

      {!props?.attachContact ? (
        <DialogActionsBar>
          <div className={`dialog-footer`}>
            {mode === MODE.VIEW && props.onDelete && (
              <Button
                className="common-btn bgBtnSecondary border-white ms-5 me-auto"
                disabled={!props.canModify}
                onClick={() =>
                  props.onDelete({ matters: gridData, mattersLoaded })
                }
              >
                Delete
              </Button>
            )}
            <Button
              className="common-btn bgBtnSecondary border-white me-3"
              onClick={() => {
                if (mode === MODE.EDIT) {
                  setMode(MODE.VIEW);
                } else {
                  props.close(hasDataChanged);
                }
              }}
              disabled={formSubmittig}
            >
              Cancel
            </Button>
            <SubmitButton
              form="contact-form"
              isLoading={formSubmittig}
              type={mode === MODE.VIEW ? "button" : "submit"}
              onClick={() => {
                if (mode === MODE.VIEW) {
                  setMode(MODE.EDIT);
                } else {
                  document.getElementById("contact-form-submit").click();
                  setIsSubmitted(true);
                }
              }}
              disabled={
                mode === MODE.EDIT
                  ? !formProps.modified
                  : mode === MODE.NEW
                    ? !formProps.allowSubmit
                    : mode === MODE.VIEW
                      ? props.canModify === false
                      : false
              }
              className="me-5"
            >
              {!props.viewMode
                ? "Add Contact"
                : mode === MODE.VIEW
                  ? "Edit"
                  : "Update Contact"}
            </SubmitButton>
          </div>
        </DialogActionsBar>
      ) : (
        <DialogActionsBar>
          <div className={`dialog-footer`}></div>
        </DialogActionsBar>
      )}
    </Dialog>
  );
};

export default AddNewContact;
